import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Checkbox } from '@atlaskit/checkbox';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { Card } from '@atlaskit/smart-card';

import { BulkIssueSelectorHeader, IssueSelectorHeader } from './header';
import { useSelectedIssues } from './utils';

type Issue = {
	issueKey: string;
	url: string;
	readonly?: boolean;
};

type IssueSelectorProps = {
	initialIssues: Issue[];
	initialSelectedIssues?: string[];
	title: string;
	isInitiallyExpanded: boolean;
	epicKey?: string;
	selectedIssuesByEpic?: Record<string, string[]>;
	onIssuesSelected?: (issueKeys: string[]) => void;
	readonly?: boolean;
};

export const IssueSelector = ({
	initialIssues,
	initialSelectedIssues,
	title,
	isInitiallyExpanded,
	epicKey,
	selectedIssuesByEpic,
	onIssuesSelected,
	readonly = false,
}: IssueSelectorProps): React.JSX.Element => {
	const [isExpanded, setIsExpanded] = useState(isInitiallyExpanded);
	const { issues, selectedIssueKeys, areAllIssuesSelected, toggleIssue, toggleIssues } =
		useSelectedIssues(initialIssues, initialSelectedIssues);

	const selectedIssuesInOtherEpics = useMemo(() => {
		return Object.entries(selectedIssuesByEpic ?? {})
			.filter(([key]) => key !== epicKey)
			.flatMap(([, selectedIssues]) => selectedIssues);
	}, [epicKey, selectedIssuesByEpic]);

	const allIssuesSelected = areAllIssuesSelected(selectedIssuesInOtherEpics ?? []);

	useEffect(() => {
		onIssuesSelected?.(selectedIssueKeys);
		// intentionally omit onIssuesSelected from dependencies, so don't force consumer to memoize
	}, [selectedIssueKeys]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleCheckboxChange = useCallback(
		(issueKey: string) => {
			toggleIssue(issueKey);
		},
		[toggleIssue],
	);

	const handleSelectAllCheckboxChange = () => {
		const issueKeysToToggle = selectedIssuesInOtherEpics
			? Object.keys(issues).filter((issueKey) => !selectedIssuesInOtherEpics.includes(issueKey))
			: Object.keys(issues);
		toggleIssues(issueKeysToToggle, !allIssuesSelected);
	};

	const handleExpandCollapse = () => {
		setIsExpanded((prev) => !prev);
	};

	const renderIssueList = () => {
		return Object.entries(issues).map(([issueKey, { url, isSelected }]) => {
			const isDisabled = readonly || selectedIssuesInOtherEpics?.includes(issueKey);
			return (
				<Inline key={issueKey} space="space.050">
					<Checkbox
						aria-label={issueKey}
						isChecked={isSelected}
						// For cases where the issue selector is used in a multi-epic context (i.e. Suggest Epics goal)
						// checkboxes should be disabled for issues that are already selected in other epics.
						isDisabled={isDisabled}
						onChange={() => handleCheckboxChange(issueKey)}
						testId="issue-selector-issue-checkbox"
					/>
					<Card url={url} appearance="inline" platform="web" showHoverPreview={true} />
				</Inline>
			);
		});
	};

	return (
		<Box>
			<Stack space="space.100">
				<IssueSelectorHeader
					title={title}
					issueState={{
						selectedIssueKeys,
						areAllIssuesSelected: allIssuesSelected,
						totalIssueCount: initialIssues.length,
					}}
					isExpanded={isExpanded}
					isDisabled={readonly}
					handleExpandCollapse={handleExpandCollapse}
					handleSelectAllCheckboxChange={handleSelectAllCheckboxChange}
				/>
				{isExpanded && renderIssueList()}
			</Stack>
		</Box>
	);
};

export const BulkIssueSelector = ({
	initialIssues,
	initialSelectedIssues,
	title,
	onIssuesSelected,
	readonly = false,
}: Omit<IssueSelectorProps, 'isInitiallyExpanded'>): React.JSX.Element => {
	const { issues, selectedIssueKeys, areAllIssuesSelected, toggleIssue, toggleIssues } =
		useSelectedIssues(initialIssues, initialSelectedIssues);

	const readonlyAndNonSelectedIssues = useMemo(() => {
		return Object.entries(issues)
			.filter(([_, issue]) => issue.readonly && !issue.isSelected)
			.map(([key]) => key);
	}, [issues]);
	const nonReadonlyIssues = useMemo(() => {
		return Object.entries(issues)
			.filter(([_, issue]) => !issue.readonly)
			.map(([key]) => key);
	}, [issues]);
	const nonReadOnlySelectedIssueKeys = useMemo(
		() =>
			Object.keys(issues).filter(
				(issueKey) => issues[issueKey].isSelected && !issues[issueKey].readonly,
			),
		[issues],
	);

	const allIssuesSelected = areAllIssuesSelected(readonlyAndNonSelectedIssues);
	const totalIssueCount = initialIssues.filter((issue) => !issue.readonly).length;

	useEffect(() => {
		onIssuesSelected?.(selectedIssueKeys);
		// intentionally omit onIssuesSelected from dependencies, so don't force consumer to memoize
	}, [selectedIssueKeys]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleCheckboxChange = useCallback(
		(issueKey: string) => {
			toggleIssue(issueKey);
		},
		[toggleIssue],
	);

	const handleSelectAllCheckboxChange = () => {
		toggleIssues(nonReadonlyIssues, !allIssuesSelected);
	};

	const renderIssueList = () => {
		const hasReadonlyUnselectedIssue = Object.values(issues).some(
			({ isSelected, readonly }) => readonly && !isSelected,
		);
		const isResumptionMigrationEnabled =
			hasReadonlyUnselectedIssue &&
			FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false);

		return Object.entries(issues).map(
			([issueKey, { url, isSelected, readonly: issueIsReadonly }]) => {
				const isDisabled = issueIsReadonly;
				const isChecked = isResumptionMigrationEnabled ? isSelected : isDisabled || isSelected;
				return (
					<Inline key={issueKey} space="space.050">
						<Checkbox
							aria-label={issueKey}
							isChecked={isChecked}
							// For cases where the issue selector is used in a multi-epic context (i.e. Suggest Epics goal)
							// checkboxes should be disabled for issues that are already selected in other epics.
							isDisabled={isDisabled}
							onChange={() => handleCheckboxChange(issueKey)}
							testId="issue-selector-issue-checkbox"
						/>
						<Card url={url} appearance="inline" platform="web" showHoverPreview={true} />
					</Inline>
				);
			},
		);
	};

	return (
		<Box>
			<Stack space="space.100">
				<BulkIssueSelectorHeader
					title={title}
					issueState={{
						selectedIssueKeys: nonReadOnlySelectedIssueKeys,
						areAllIssuesSelected: allIssuesSelected,
						totalIssueCount,
					}}
					isDisabled={readonly}
					handleSelectAllCheckboxChange={handleSelectAllCheckboxChange}
				/>
				{renderIssueList()}
			</Stack>
		</Box>
	);
};
